import type { FC } from "react";
import { useCallback, useEffect } from "react";

import { DEPRECATED_Loading } from "@ui/components/DEPRECATED_Loading";
import { useTranslations } from "@ui/providers/useTranslations";
import dompurify from "isomorphic-dompurify";

import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { Button } from "@jti/core-components/components/Button";
import { usePolling } from "@jti/rest-api";

import styles from "./AsyncValidationPopup.module.scss";

const { sanitize } = dompurify;

export type AsyncValidationPopupProps = {
  isOpened?: boolean;
  isError?: boolean;
  onRefresh?: () => void;
  onTryAgain?: () => void;
  onCancel?: () => void;
  onContinueWithIdVerification?: () => void;
  isContinueWithIdVerificationDisabled?: boolean;
};

export const AsyncValidationPopup: FC<AsyncValidationPopupProps> = ({
  onContinueWithIdVerification,
  onTryAgain,
  onCancel,
  onRefresh,
  isContinueWithIdVerificationDisabled,
  isOpened,
  isError,
}) => {
  const { __ } = useTranslations();
  const { startPolling, stopPolling } = usePolling();

  const mainInformationText = isError
    ? __("ageverification.async_popup.error_message")
    : __("ageverification.async_popup.description");

  const handlePollingAvData = useCallback(() => {
    if (!isOpened || isError) {
      stopPolling();
      return;
    }
    stopPolling(); // clear previous interval to not loose reference to it
    if (onRefresh) startPolling(onRefresh, 5000);
  }, [isError, isOpened, onRefresh, startPolling, stopPolling]);

  useEffect(() => {
    handlePollingAvData();
    return () => stopPolling();
  }, [handlePollingAvData, stopPolling]);

  if (!isOpened) {
    return null;
  }

  return (
    <div className={styles.popupWrapper} data-testid="async-av-validation-popup">
      <div className={styles.popupContainer}>
        <div className={styles.headline}>{__("ageverification.async_popup.headline")}</div>

        <button type="button" onClick={onCancel} className={styles.closeIcon} aria-label={__("common.close")}>
          <UiCrossIcon width="15px" height="15px" aria-hidden="true" />
        </button>
        <p className={styles.popupDescription} dangerouslySetInnerHTML={{ __html: sanitize(mainInformationText) }} />
        {!isError && <DEPRECATED_Loading dataTestId="async-av-validation-popup-loading" />}
        <div className={styles.popupActionBar}>
          <Button className={styles.actionButton} variant="secondary" onClick={onCancel} disabled={!isError}>
            {__("ageverification.async_popup.button.cancel")}
          </Button>
          <Button className={styles.actionButton} variant="primary" onClick={onTryAgain} pending={!isError}>
            {__("ageverification.async_popup.button.try_again")}
          </Button>
          {onContinueWithIdVerification && (
            <Button
              className={styles.actionButton}
              variant="primary"
              disabled={isContinueWithIdVerificationDisabled}
              onClick={onContinueWithIdVerification}
            >
              {__("ageverification.age_estimation_fallback.cta")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
